/**
 * オフライン決済キュー Context
 * アプリ全体でオフラインキューの状態を管理
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import offlineQueue from '../services/offlinePaymentQueue';
import api from '../services/api';

const OfflineQueueContext = createContext(null);

/**
 * 決済データをサーバーに同期する関数
 * H-004 FIX: localIdを送信して冪等性を確保
 */
const syncPaymentToServer = async (payment) => {
  try {
    const storeId = payment.storeId;
    // H-004 FIX: localIdとして保存されたIDを使用（IndexedDBのauto-incrementedキー）
    const localId = payment.id ? `offline_${payment.id}_${payment.createdAt}` : `offline_${Date.now()}`;

    const response = await api.post(`/api/stores/${storeId}/sales`, {
      ...payment,
      isOfflineSync: true, // オフライン同期であることを示すフラグ
      localId, // H-004 FIX: 冪等性キー
      originalCreatedAt: payment.createdAt,
    });

    // H-004 FIX: 冪等性による重複検出の場合もsuccessとして扱う
    if (response.data?.idempotent) {
      console.log('[OfflineSync] 冪等性により重複を検出、既存データを使用');
    }

    return {
      success: true,
      data: response.data,
      idempotent: response.data?.idempotent || false,
    };
  } catch (error) {
    console.error('[OfflineSync] サーバー同期エラー:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message,
    };
  }
};

export const OfflineQueueProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [queueSummary, setQueueSummary] = useState({
    total: 0,
    pending: 0,
    synced: 0,
    failed: 0,
    totalAmount: 0,
    pendingAmount: 0,
  });
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [syncController, setSyncController] = useState(null);

  // キューサマリを更新
  const updateQueueSummary = useCallback(async () => {
    try {
      const summary = await offlineQueue.getQueueSummary();
      setQueueSummary(summary);
    } catch (error) {
      console.error('[OfflineQueue] サマリ取得エラー:', error);
    }
  }, []);

  // 同期を実行
  const syncNow = useCallback(async () => {
    if (isSyncing || !isOnline) return;

    setIsSyncing(true);
    try {
      const result = await offlineQueue.syncPendingPayments(syncPaymentToServer);
      setLastSyncTime(new Date());
      await updateQueueSummary();
      return result;
    } finally {
      setIsSyncing(false);
    }
  }, [isSyncing, isOnline, updateQueueSummary]);

  // 決済をキューに追加
  const enqueuePayment = useCallback(async (paymentData) => {
    const id = await offlineQueue.enqueue(paymentData);
    await updateQueueSummary();

    // オンラインなら即座に同期を試みる
    if (isOnline) {
      setTimeout(() => syncNow(), 1000);
    }

    return id;
  }, [isOnline, syncNow, updateQueueSummary]);

  // REMAIN-1 FIX: オンライン状態の監視（復帰時に自動同期を強化）
  useEffect(() => {
    const handleOnline = async () => {
      setIsOnline(true);
      console.log('[OfflineQueue] オンライン復帰 - 自動同期を開始');

      // REMAIN-1 FIX: オンライン復帰時に即座に同期を実行（UI状態も更新）
      // 少し遅延を入れてネットワーク安定を待つ
      setTimeout(async () => {
        try {
          setIsSyncing(true);
          const summary = await offlineQueue.getQueueSummary();
          if (summary.pending > 0) {
            console.log(`[OfflineQueue] ${summary.pending}件の保留中決済を同期開始`);
            const result = await offlineQueue.syncPendingPayments(syncPaymentToServer);
            console.log('[OfflineQueue] 自動同期完了:', result);
            setLastSyncTime(new Date());
          }
          await updateQueueSummary();
        } catch (error) {
          console.error('[OfflineQueue] 自動同期エラー:', error);
        } finally {
          setIsSyncing(false);
        }
      }, 2000); // 2秒待機してネットワーク安定化
    };

    const handleOffline = () => {
      setIsOnline(false);
      console.log('[OfflineQueue] オフラインになりました');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [updateQueueSummary]);

  // 定期同期の開始
  useEffect(() => {
    const controller = offlineQueue.startPeriodicSync(async (payment) => {
      const result = await syncPaymentToServer(payment);
      await updateQueueSummary();
      return result;
    });

    setSyncController(controller);

    // 初回サマリ更新
    updateQueueSummary();

    return () => {
      if (controller?.stop) {
        controller.stop();
      }
    };
  }, [updateQueueSummary]);

  // クリーンアップ（古いデータの削除）
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      offlineQueue.cleanup().then(updateQueueSummary);
    }, 24 * 60 * 60 * 1000); // 24時間ごと

    // 初回クリーンアップ
    offlineQueue.cleanup();

    return () => clearInterval(cleanupInterval);
  }, [updateQueueSummary]);

  const value = {
    isOnline,
    queueSummary,
    isSyncing,
    lastSyncTime,
    enqueuePayment,
    syncNow,
    updateQueueSummary,
    getPendingPayments: offlineQueue.getPendingPayments,
    getAllPayments: offlineQueue.getAllPayments,
  };

  return (
    <OfflineQueueContext.Provider value={value}>
      {children}
    </OfflineQueueContext.Provider>
  );
};

/**
 * オフラインキューフック
 */
export const useOfflineQueue = () => {
  const context = useContext(OfflineQueueContext);
  if (!context) {
    throw new Error('useOfflineQueue must be used within OfflineQueueProvider');
  }
  return context;
};

export default OfflineQueueContext;
